"use client"

import * as React from "react"

import {
  getStorageItem,
  setStorageItem,
  subscribeToStorageKey,
} from "@exxatdesignux/ui/lib/persisted-state"

import {
  getTermById,
  getTermsByPosition,
  MOCK_EVALUATION_TERMS,
  type EvaluationTerm,
} from "@/lib/mock/evaluation-dashboard"
import { productPersistKey } from "@/stores/app-store"

const STORAGE_KEY = productPersistKey("exxat-surveys", "quality-dashboard-term")
const VERSION = 1

const listeners = new Set<() => void>()
let snapshot: string | null = null
let hydrated = false

/** All evaluation terms for the quality dashboard term picker. */
export function qualityDashboardTerms(): EvaluationTerm[] {
  return [...MOCK_EVALUATION_TERMS].sort((a, b) => b.startDate.localeCompare(a.startDate))
}

function defaultTermId(): string {
  return getTermsByPosition("current")[0]?.id ?? qualityDashboardTerms()[0]?.id ?? "fall-2025"
}

function subscribe(onChange: () => void) {
  listeners.add(onChange)
  const unsubscribeStorage = subscribeToStorageKey(STORAGE_KEY, () => {
    hydrated = false
    onChange()
  })
  return () => {
    listeners.delete(onChange)
    unsubscribeStorage()
  }
}

function readTermId(): string {
  if (hydrated && snapshot) return snapshot
  hydrated = true

  const raw = getStorageItem(STORAGE_KEY)
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw)
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        (parsed as { v?: number }).v === VERSION
      ) {
        const id = (parsed as { d?: unknown }).d
        if (typeof id === "string" && getTermById(id)) {
          snapshot = id
          return snapshot
        }
      }
    } catch {
      // fall through
    }
  }

  snapshot = defaultTermId()
  return snapshot
}

export function setQualityDashboardTermId(next: string) {
  snapshot = next
  hydrated = true
  setStorageItem(STORAGE_KEY, JSON.stringify({ v: VERSION, d: next }))
  for (const listener of listeners) listener()
}

export function useQualityDashboardTerm(): {
  termId: string
  term: EvaluationTerm | undefined
  terms: EvaluationTerm[]
  setTermId: (id: string) => void
} {
  const termId = React.useSyncExternalStore(subscribe, readTermId, defaultTermId)
  const terms = React.useMemo(() => qualityDashboardTerms(), [])
  const term = React.useMemo(() => getTermById(termId), [termId])

  return {
    termId,
    term,
    terms,
    setTermId: setQualityDashboardTermId,
  }
}
